import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { FileSignature, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import axios from "axios";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

export default function SignContractPage() {
  const { token } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signerName, setSignerName] = useState("");
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    fetchContract();
  }, [token]);

  const fetchContract = async () => {
    try {
      const res = await axios.get(`${API}/public/sign-contract/${token}`);
      setContract(res.data);
      if (res.data.status === "signed") setSigned(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Lien invalide ou expire");
    } finally { setLoading(false); }
  };

  const handleSign = async () => {
    if (!signerName.trim()) return;
    setSigning(true);
    try {
      await axios.post(`${API}/public/sign-contract/${token}`, { signer_name: signerName });
      setSigned(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de la signature");
    } finally { setSigning(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center px-4">
      <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-8 max-w-md w-full text-center">
        <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-white mb-2">Erreur</h1>
        <p className="text-[#94A3B8]">{error}</p>
      </div>
    </div>
  );

  if (signed) return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center px-4">
      <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-8 max-w-md w-full text-center" data-testid="signed-success">
        <CheckCircle className="h-16 w-16 text-emerald-400 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Contrat signe</h1>
        <p className="text-[#94A3B8]">Merci, votre signature electronique a ete enregistree avec succes.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center px-4 py-8" data-testid="sign-contract-page">
      <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-8 max-w-2xl w-full">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 rounded-xl bg-[#0099CC]/10">
            <FileSignature className="h-8 w-8 text-[#0099CC]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">{contract?.title}</h1>
            <p className="text-[#94A3B8] text-sm">Client: {contract?.client_name}</p>
          </div>
        </div>

        <div className="space-y-4 mb-6">
          {contract?.services && (
            <div className="bg-[#0B0F19] rounded-lg p-4 border border-[#2A3441]">
              <h3 className="text-[#64748b] text-xs uppercase mb-2">Services</h3>
              <p className="text-[#94A3B8] text-sm whitespace-pre-line">{contract.services}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            {contract?.amount > 0 && (
              <div className="bg-[#0B0F19] rounded-lg p-4 border border-[#2A3441]">
                <h3 className="text-[#64748b] text-xs uppercase mb-1">Montant</h3>
                <p className="text-white font-semibold">{contract.amount.toLocaleString("fr-FR")} EUR HT</p>
              </div>
            )}
            {contract?.duration && (
              <div className="bg-[#0B0F19] rounded-lg p-4 border border-[#2A3441]">
                <h3 className="text-[#64748b] text-xs uppercase mb-1">Duree</h3>
                <p className="text-white font-semibold">{contract.duration}</p>
              </div>
            )}
          </div>
          {contract?.conditions && (
            <div className="bg-[#0B0F19] rounded-lg p-4 border border-[#2A3441]">
              <h3 className="text-[#64748b] text-xs uppercase mb-2">Conditions</h3>
              <p className="text-[#94A3B8] text-sm whitespace-pre-line">{contract.conditions}</p>
            </div>
          )}
        </div>

        <div className="border-t border-[#2A3441] pt-6 space-y-4">
          <h3 className="text-white font-semibold">Signer ce contrat</h3>
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Votre nom complet</Label>
            <Input value={signerName} onChange={(e) => setSignerName(e.target.value)}
              placeholder="Nom et prenom du signataire"
              className="bg-[#0B0F19] border-[#2A3441] text-white" data-testid="signer-name-input" />
          </div>
          <p className="text-[#64748b] text-xs">En signant, vous acceptez les termes et conditions ci-dessus. Votre adresse IP et la date seront enregistrees comme preuve de signature.</p>
          <Button onClick={handleSign} disabled={signing || !signerName.trim()}
            className="w-full bg-[#0099CC] hover:bg-[#0088B5] text-white py-3" data-testid="sign-contract-btn">
            {signing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileSignature className="h-4 w-4 mr-2" />}
            Signer electroniquement
          </Button>
        </div>
      </div>
    </div>
  );
}
